import { useCallback, useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
  /** Whether there are more items to load */
  hasMore: boolean;
  /** Whether currently loading */
  isLoading: boolean;
  /** Callback to load more items */
  onLoadMore: () => void;
  /** Threshold in pixels before bottom to trigger load (default: 200) */
  threshold?: number;
  /** Root element for intersection observer (default: null = viewport) */
  root?: Element | null;
}

interface UseInfiniteScrollReturn {
  /** Ref to attach to the sentinel element at the bottom of the list */
  sentinelRef: React.RefObject<HTMLDivElement>;
  /** Ref to attach to the scroll container (optional, for scroll-to-top) */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Scroll to top of the list */
  scrollToTop: () => void;
}

/**
 * Custom hook for infinite scroll using Intersection Observer API.
 * More performant and reliable than scroll event listeners.
 *
 * Usage:
 * ```tsx
 * const { sentinelRef, scrollToTop } = useInfiniteScroll({
 *   hasMore,
 *   isLoading,
 *   onLoadMore: () => dispatch(incrementOffset())
 * });
 *
 * return (
 *   <div>
 *     {items.map(item => <Item key={item.id} />)}
 *     <div ref={sentinelRef} /> {/* Sentinel element *\/}
 *   </div>
 * );
 * ```
 */
export const useInfiniteScroll = ({
  hasMore,
  isLoading,
  onLoadMore,
  threshold = 200,
  root = null
}: UseInfiniteScrollOptions): UseInfiniteScrollReturn => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const scrollToTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Don't observe if no more items or currently loading
    if (!hasMore || isLoading) {
      return;
    }

    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }

    // Create intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      {
        root,
        rootMargin: `${threshold}px`,
        threshold: 0
      }
    );

    observerRef.current.observe(sentinel);

    // Cleanup on unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoading, onLoadMore, threshold, root]);

  return {
    sentinelRef,
    containerRef,
    scrollToTop
  };
};

export default useInfiniteScroll;
